import { useEffect, useMemo, useRef, useState } from 'react'
import { route } from 'routes-gen'
import { z } from 'zod'

import { type ActionFunctionArgs, json, redirect } from '@remix-run/node'
import { useActionData, useNavigation, useSubmit } from '@remix-run/react'

import type { Submission } from '@conform-to/react'
import { parse } from '@conform-to/zod'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { getEABaseUrl } from '~/utils/context.server'
import { ApiResponseErrorEA, getEAApi } from '~/utils/api.server'
import { useClientEnv } from '~/utils/client/hooks/useClientEnv'
import { createRegisterCookie } from '~/utils/register.server'

import {
  Modal,
  Input,
  Label,
  Title,
  Button,
  Paragraph,
  ModalTitle,
  ModalContent,
  UnsavedChangesModal,
  ModalDismissButton,
  AlertBannerUnexpectedError,
} from '~/components'
import type { PageHandle } from '~/components/convenience/PageHandle'
import { useUnsavedChangesWarning } from './kundenzugang.anlegerfragebogen.$depotId.neu/hooks/useUnsavedChangesWarning'

import { SERVER_ENV } from 'env.server'
import { createLoginNameSchemaBase } from '~/utils/form-validation'
import { i18n } from '@lingui/core'

const createClientSchema = () =>
  z.object({
    loginName: createLoginNameSchemaBase(),
  })

type ClientSchema = z.infer<ReturnType<typeof createClientSchema>>

export const action = async ({ request, context }: ActionFunctionArgs) => {
  const formData = await request.formData()
  if (formData.get('loginName') === '000000000' || formData.get('loginName') === '00000000') {
    throw new Error('Unexpected error')
  }

  try {
    const serverSchema = z.object({
      loginName: createLoginNameSchemaBase().refine(async (loginName) => {
        const api = getEAApi(await getEABaseUrl({ request, context }))
        try {
          await api('/Register/LoginNameValidieren', {
            method: 'POST',
            headers: {
              authorization: `Bearer ${SERVER_ENV.API_TOKEN}`,
            },
            body: { loginName },
          })
          return true
        } catch (error: unknown) {
          console.error(`login name api error: "${error}".`)
          if (
            error instanceof ApiResponseErrorEA &&
            error.data.fehlerCode === 'LOGIN_NAME_NOT_FOUND'
          ) {
            return false
          }
          throw error
        }
      }, i18n._('Der Loginname konnte nicht gefunden werden.')),
    })

    const validation = await parse(formData, { schema: serverSchema, async: true })

    if (!validation.value || validation.intent !== 'submit') {
      return json(validation, { status: 400 })
    }

    const loginName = validation.value.loginName

    return redirect(route('/erstanmeldung/schritt/identifikation'), {
      headers: new Headers({
        'Set-Cookie': await createRegisterCookie(request)({ loginName }),
      }),
    })
  } catch (error: unknown) {
    const unexpectedError =
      error instanceof ApiResponseErrorEA && error.data.technischerFehlerText
        ? error.data.technischerFehlerText
        : 'SY'
    const validation: Submission<{ loginName: string; unexpected: string }> = {
      intent: 'submit',
      payload: {},
      error: {
        unexpected: [unexpectedError],
      },
    }
    return json(validation, { status: 500 })
  }
}

export const handle: PageHandle = {
  bodyClassName: 'h-screen bg-white flex flex-col',
}

export default function RegisterAnmeldungRoute() {
  const navigation = useNavigation()
  const clientEnv = useClientEnv()
  const actionData = useActionData<typeof action>()
  const submit = useSubmit()
  const formRef = useRef<HTMLFormElement>(null)

  const clientSchema = useMemo(() => createClientSchema(), [])

  const form = useForm<ClientSchema>({
    resolver: zodResolver(clientSchema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  })

  const { blocker, markSubmitted } = useUnsavedChangesWarning(form.watch)

  useEffect(() => {
    if (!actionData?.error) return
    Object.entries(actionData.error).forEach(([key, messages]) => {
      if (key !== 'unexpected' && messages?.[0]) {
        form.setError(key as keyof ClientSchema, { message: messages[0] })
      }
    })
  }, [actionData, form])

  const onSubmit = () => {
    markSubmitted()
    submit(formRef.current)
  }

  const [displayHelper, setDisplayHelper] = useState<boolean>(false)
  return (
    <>
      <UnsavedChangesModal blocker={blocker} />
      <main className="flex-1 pb-4">
        <form ref={formRef} method="post" onSubmit={form.handleSubmit(onSubmit)}>
          <Title className="mb-4">{i18n._('Anmeldung')}</Title>
          <Paragraph>
            {i18n._('Im Folgenden prüfen wir, ob Ihr Loginname gefunden werden kann.')}
          </Paragraph>
          {actionData?.error?.unexpected && (
            <AlertBannerUnexpectedError errorCode={actionData.error.unexpected} />
          )}
          <div className="my-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            <div className="sm:col-span-3">
              <Input
                title={i18n._('Loginname')}
                label={i18n._('Loginname')}
                error={form.formState.errors.loginName?.message}
                helper={i18n._('Wo finde ich meinen Loginnamen?')}
                placeholder={i18n._('Ihr Loginname')}
                onHelper={() => setDisplayHelper(true)}
                {...form.register('loginName')}
              />
            </div>
            <Modal visible={displayHelper} onClose={() => setDisplayHelper(false)}>
              <ModalTitle text={i18n._('Wo finde ich meinen Loginnamen?')} headingLevel={2} />
              <ModalContent>
                <div className="my-4">
                  <Paragraph>
                    {i18n._(
                      'Ihre Loginnummer finden Sie in dem Depoteröffnungsschreiben, welches Sie erhalten haben sollten.'
                    )}
                  </Paragraph>
                  <br />
                  <Label>{i18n._('Beispiel-Dokument')}</Label>
                  <br />
                  <br />
                  <div className="w-full">
                    <img
                      className="object-cover"
                      src={`${clientEnv.publicPath}/images/erstanmeldung/erstanmeldung-depoteinrichtung-brief-design.png`}
                      alt={i18n._('Beispiel Dokument für das Depoteröffnungsschreiben')}
                    />
                  </div>
                </div>
              </ModalContent>
              <ModalDismissButton text={i18n._('Verstanden')} />
            </Modal>
          </div>
          <Button
            variant="primary"
            label={i18n._('Weiter')}
            type="submit"
            align="left"
            loading={navigation.state !== 'idle'}
            className="w-full md:w-40"
            // Keeps focus on the field: its blur revalidation would shift the button mid-click.
            onMouseDown={(e) => e.preventDefault()}
          />
        </form>
      </main>
    </>
  )
}
